import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface QuizQuestion {
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

const quizQuestions: QuizQuestion[] = [
  {
    question: "Why is nutrition particularly important during pregnancy?",
    options: [
      "To gain as much weight as possible",
      "To provide essential nutrients for fetal development",
      "To eat for two separate people",
      "To avoid all vegetables"
    ],
    correctAnswer: 1,
    explanation: "Pregnancy requires specific nutrients like folate, iron, and calcium for proper fetal development. Good nutrition during pregnancy supports healthy growth and reduces the risk of birth defects and complications."
  },
  {
    question: "Which mineral is especially important for older adults and can be found in fish and fortified foods?",
    options: [
      "Iron",
      "Vitamin B12",
      "Calcium",
      "Zinc"
    ],
    correctAnswer: 1,
    explanation: "Vitamin B12 is crucial for older adults as absorption decreases with age. Good sources include fish, fortified cereals, and supplements when needed. B12 supports nerve function and red blood cell production."
  },
  {
    question: "For someone with diabetes, which foods should they focus on including in their diet?",
    options: [
      "Only sugary foods",
      "High-fiber foods, lean proteins, complex carbohydrates",
      "Only white rice and bread",
      "Fried foods and sweets"
    ],
    correctAnswer: 1,
    explanation: "People with diabetes should focus on high-fiber foods, lean proteins, and complex carbohydrates. These help maintain stable blood sugar levels and provide sustained energy without causing spikes."
  },
  {
    question: "What is a key nutritional challenge for vegetarians?",
    options: [
      "Getting enough vegetables",
      "Obtaining sufficient protein, B12, and iron",
      "Eating too many vegetables",
      "Avoiding all animal products"
    ],
    correctAnswer: 1,
    explanation: "Vegetarians need to ensure they get enough protein, vitamin B12, and iron from plant-based sources. Legumes, fortified foods, and careful meal planning help meet these nutritional needs."
  },
  {
    question: "Why should nutrition be personalized according to the module?",
    options: [
      "Everyone has the same nutritional needs",
      "Different health conditions and life stages require specific approaches",
      "Only athletes need special nutrition",
      "Children and adults eat the same foods"
    ],
    correctAnswer: 1,
    explanation: "Nutrition should be personalized because different health conditions (like diabetes, hypertension) and life stages (pregnancy, older adulthood) have unique nutritional requirements. What works for one person may not be appropriate for another."
  }
];

const NutritionalModule6QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('nutritionalM6');
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [responses, setResponses] = useState<{[key: number]: number}>({});
  const [showCompletion, setShowCompletion] = useState(false);

  const handleAnswerSelect = (answerIndex: number) => {
    if (responses[currentQuestion] === undefined) {
      setSelectedAnswer(answerIndex);
      setShowExplanation(true);
      setResponses(prev => ({...prev, [currentQuestion]: answerIndex}));
    }
  };

  const handleNext = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
      setSelectedAnswer(null);
      setShowExplanation(false);
      saveProgress(nextQuestion + 1);
    } else {
      saveProgress(quizQuestions.length, true);
      setShowCompletion(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      const prevQuestion = currentQuestion - 1;
      const previousResponse = responses[prevQuestion];
      setCurrentQuestion(prevQuestion);
      setSelectedAnswer(previousResponse ?? null);
      setShowExplanation(previousResponse !== undefined);
      saveProgress(prevQuestion + 1);
    }
  };

  const handleExit = () => {
    navigate('/panel/learn/nutritional-resources');
  };

  if (showCompletion) {
    return (
      <div className="quiz-completion-section">
        <h2 className="completion-title">
          <span>Congratulations!</span>
        </h2>
        <p className="completion-subtitle">
          You've successfully completed the Module 6 quiz!
        </p>

        <div className="completion-card">
          <div className="achievement-section">
            <h3 className="section-title">
              <span>Key Achievements</span>
            </h3>
            <p className="achievement-intro">You've demonstrated a great understanding of:</p>
            <ul className="achievement-list">
              <li>
                <span className="check"></span>
                <span>Nutrition for different health conditions</span>
              </li>
              <li>
                <span className="check"></span>
                <span>Life stage-specific nutritional needs</span>
              </li>
              <li>
                <span className="check"></span>
                <span>Adapting diets for special requirements</span>
              </li>
            </ul>
          </div>

          <div className="next-steps-section">
            <h3 className="section-title">
              <span>Next Steps</span>
            </h3>
            <p>Consider how nutrition applies to your specific health needs!</p>
            <p>Ready to continue your wellness journey?</p>
          </div>

          <button
            className="resource-button"
            onClick={handleExit}
          >
            Return to Nutritional Resources
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading quiz...</p>
      </div>
    );
  }

  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;
  const currentQuiz = quizQuestions[currentQuestion];

  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left nutritional-panel">
          <div className="module-header">
            <h1>Module 6 Quiz</h1>
            <p className="module-subtitle">Integrating Nutrition into Holistic Health</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Let's review what we've learned about integrating nutrition into holistic health.
              There's no scoring - this is just to reinforce the main ideas!
            </p>
          </div>
        </div>

        <div className="assessment-right-panel">
          <div className="assessment-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Question {currentQuestion + 1} of {quizQuestions.length}
              </span>
            </div>

            <div className="question-section">
              <h2 className="question-title">
                {currentQuiz.question}
              </h2>
              <p className="question-instruction">
                Please select the answer that best matches your understanding.
              </p>

              <div className="answer-options">
                {currentQuiz.options.map((option, index) => (
                  <label key={index} className="answer-option">
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={index}
                      checked={selectedAnswer === index}
                      onChange={() => handleAnswerSelect(index)}
                      disabled={selectedAnswer !== null}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>

              {showExplanation && (
                <div className="explanation">
                  <h3>{selectedAnswer === currentQuiz.correctAnswer ? 'Correct!' : 'Remember:'}</h3>
                  <p>{currentQuiz.explanation}</p>
                </div>
              )}
            </div>
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={currentQuestion === 0 ? handleExit : handleBack}
            >
              {currentQuestion === 0 ? 'Exit Quiz' : 'Previous Question'}
            </button>

              {currentQuestion < quizQuestions.length - 1 ? (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Next Question
                </button>
              ) : (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Complete Quiz
                </button>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NutritionalModule6QuizPage;

